import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Stepper, { StepperItem } from "../../../commons/Stepper";
import Button from "../../../commons/Button";
import { LESSONS_DATABASE } from "../../../../constants/education";
import { postGoldenChallengeChain } from "../../../../services/webServices/challenge/post";
import { patchGoldenChallengeChain } from "../../../../services/webServices/challenge/patch";
import { getGoldenChallengeChainById } from "../../../../services/webServices/challenge/get";
import StepDayQuestions from "./StepDayQuestions";
import StepSchedule from "./StepSchedule";
import StepPreview from "./StepPreview";
import {
  createInitialChainFormState,
  buildChainSubmissionBody,
  buildChallengeUpdateBody,
  mapChainToFormState,
  isChainDayComplete,
} from "./wizardUtils";
import type { ChainFormState } from "./wizardTypes";
import type { GoldenChallengeChain } from "../../../../services/webServices/challenge/types";

const ALL_LESSONS = Object.values(LESSONS_DATABASE).flatMap((gradesForStage) =>
  Object.values(gradesForStage).flat(),
);

type Props = {
  challengeId?: string;
  initialChain?: GoldenChallengeChain;
};

export default function ChallengeChainWizard({
  challengeId,
  initialChain,
}: Props) {
  const navigate = useNavigate();
  const isEditMode = Boolean(challengeId);

  const steps: StepperItem[] = [
    { id: "content", label: isEditMode ? "ویرایش زنجیره" : "افزودن زنجیره جدید" },
    { id: "schedule", label: "انتخاب زمان اجرا" },
    { id: "preview", label: "پیش‌نمایش" },
  ];

  const [state, setState] = useState<ChainFormState>(() =>
    initialChain
      ? mapChainToFormState(initialChain)
      : createInitialChainFormState(),
  );
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitLockRef = useRef(false);
  const [isLoadingChallenge, setIsLoadingChallenge] = useState(
    isEditMode && !initialChain,
  );

  useEffect(() => {
    if (!challengeId || initialChain) return;

    let isActive = true;

    (async () => {
      const chain = await getGoldenChallengeChainById({ id: challengeId });

      if (!isActive) return;

      if (!chain) {
        toast.error("زنجیره مورد نظر یافت نشد");
        navigate("/challenge");
        return;
      }

      const mapped = mapChainToFormState(chain);
      setState(mapped);
      setIsLoadingChallenge(false);
    })();

    return () => {
      isActive = false;
    };
  }, [challengeId, initialChain, navigate]);

  const lessonLabel =
    ALL_LESSONS.find((lesson) => lesson.value === state.lesson)?.label ??
    state.lesson;

  const isContentStepValid = useMemo(() => {
    if (!state.lesson || !state.grade) return false;

    if (isEditMode) {
      const editedDay = state.days.find(
        (day) => day.weekDay === state.activeWeekDay,
      );
      return Boolean(editedDay && isChainDayComplete(editedDay));
    }

    return state.days.some(isChainDayComplete);
  }, [state, isEditMode]);

  const isScheduleStepValid = Boolean(state.dateRange);

  const goBack = () => navigate("/challenge");

  const handleContinue = () => {
    if (currentStep === 0 && !isContentStepValid) return;
    if (currentStep === 1 && !isScheduleStepValid) return;
    setCurrentStep((step) => Math.min(step + 1, steps.length - 1));
  };

  const handleStepClick = (index: number) => {
    if (index < currentStep) setCurrentStep(index);
  };

  const submitChain = async (status: "draft" | "active") => {
    if (submitLockRef.current) return;

    submitLockRef.current = true;

    try {
      if (isEditMode) {
        await submitEdit(status);
        return;
      }

      const body = buildChainSubmissionBody(state, status);

      if (!body) {
        toast.error("حداقل یک سوال برای یکی از روزها انتخاب کنید");
        return;
      }

      setIsSubmitting(true);

      const success = await postGoldenChallengeChain({ body });

      if (!success) {
        toast.error("ثبت زنجیره با خطا مواجه شد");
        return;
      }

      toast.success(
        status === "draft"
          ? "زنجیره به‌صورت پیش‌نویس ذخیره شد"
          : "زنجیره با موفقیت منتشر شد",
      );
      goBack();
    } finally {
      submitLockRef.current = false;
      setIsSubmitting(false);
    }
  };

  const submitEdit = async (status: "draft" | "active") => {
    if (!challengeId) return;

    const body = buildChallengeUpdateBody(state, status, state.activeWeekDay);

    if (!body) {
      toast.error("سوال و بازه زمانی چالش را کامل کنید");
      return;
    }

    setIsSubmitting(true);
    const success = await patchGoldenChallengeChain({ id: challengeId, body });

    if (!success) {
      toast.error("ویرایش زنجیره با خطا مواجه شد");
      return;
    }

    toast.success("زنجیره با موفقیت ویرایش شد");
    goBack();
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <StepDayQuestions
            state={state}
            disabled={isSubmitting}
            onChange={setState}
          />
        );
      case 1:
        return (
          <StepSchedule
            state={state}
            disabled={isSubmitting}
            onChange={setState}
          />
        );
      default:
        return <StepPreview state={state} lessonLabel={lessonLabel} />;
    }
  };

  const isLastStep = currentStep === steps.length - 1;
  const canContinue =
    currentStep === 0 ? isContentStepValid : isScheduleStepValid;

  if (isLoadingChallenge) {
    return (
      <div className="flex w-full items-center justify-center rounded-2xl border card-shadow bg-white p-16">
        <p className="text-sm text-gray-500">در حال بارگذاری اطلاعات زنجیره...</p>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col rounded-2xl border card-shadow bg-white">
      <div className="border-b bg-white px-6">
        <Stepper
          steps={steps}
          currentStep={currentStep}
          ariaLabel={isEditMode ? "مراحل ویرایش زنجیره" : "مراحل افزودن زنجیره"}
          onStepClick={handleStepClick}
        />
      </div>

      <div className="p-6">{renderStep()}</div>

      <div className="flex items-center justify-end gap-3 px-6 py-4" dir="rtl">
        {isLastStep ? (
          <Button
            variant="primary"
            style="solid"
            size="sm"
            text={isEditMode ? "ذخیره تغییرات" : "انتشار زنجیره"}
            loading={isSubmitting}
            disabled={
              isSubmitting || !isContentStepValid || !isScheduleStepValid
            }
            onClick={() => submitChain("active")}
          />
        ) : (
          <Button
            variant="primary"
            style="solid"
            size="sm"
            text="تایید و ادامه"
            loading={false}
            disabled={isSubmitting || !canContinue}
            onClick={handleContinue}
          />
        )}
        <Button
          variant="primary"
          style="outline"
          size="sm"
          text="ذخیره پیش‌نویس"
          loading={false}
          disabled={isSubmitting}
          onClick={() => submitChain("draft")}
        />
      </div>
    </div>
  );
}
